import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ArrowLeft, Wallet, TrendingUp, TrendingDown, DollarSign, 
  CalendarIcon, Settings, Filter, RefreshCw, Pencil, Trash2,
  Receipt, Gift, ArrowDownLeft, ArrowUpRight
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = ({ forcedGestorId = null }) => {
  const params = useParams();
  const gestorId = forcedGestorId || params.gestorId;
  const navigate = useNavigate();
  const { getAuthHeaders, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [gestor, setGestor] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [isAdjustmentDialogOpen, setIsAdjustmentDialogOpen] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({ amount: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [editingTxn, setEditingTxn] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', description: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchData();
  }, [gestorId, filterType, dateRange]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      let params = new URLSearchParams();
      if (filterType !== 'all') params.append('transaction_type', filterType);
      if (dateRange.start) params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange.end) params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      
      const [summaryRes, transactionsRes, gestorRes] = await Promise.all([
        axios.get(`${API_URL}/wallet/${gestorId}/summary`, { headers }),
        axios.get(`${API_URL}/wallet/${gestorId}/transactions?${params.toString()}&limit=200`, { headers }),
        axios.get(`${API_URL}/gestores/${gestorId}`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
      setGestor(gestorRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/wallet/adjustment`, {
        gestor_id: gestorId,
        amount: parseFloat(adjustmentForm.amount),
        description: adjustmentForm.description
      }, { headers: getAuthHeaders() });
      toast.success('Ajuste registrado');
      setIsAdjustmentDialogOpen(false);
      setAdjustmentForm({ amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al registrar ajuste');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTxn = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updateData = {};
      if (editForm.amount !== '') updateData.amount = parseFloat(editForm.amount);
      if (editForm.description !== '') updateData.description = editForm.description;
      
      await axios.put(`${API_URL}/wallet/transactions/${editingTxn.id}`, updateData, { headers: getAuthHeaders() });
      toast.success('Transacción actualizada');
      setEditingTxn(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar transacción');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTxn = async (txnId) => {
    try {
      await axios.delete(`${API_URL}/wallet/transactions/${txnId}`, { headers: getAuthHeaders() });
      toast.success('Transacción eliminada');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar transacción');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2
    }).format(Math.abs(value));
  };

  const getTransactionIcon = (type, amount) => {
    if (type === 'COMISION') return <Receipt className="w-4 h-4 text-orange-500" />;
    if (type === 'BONO') return <Gift className="w-4 h-4 text-purple-500" />;
    if (amount >= 0) return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
    return <ArrowUpRight className="w-4 h-4 text-red-500" />;
  };

  const getTransactionBadge = (type) => {
    const styles = {
      'VENTA': 'bg-red-100 text-red-800',
      'PAGO': 'bg-green-100 text-green-800',
      'COMISION': 'bg-orange-100 text-orange-800',
      'BONO': 'bg-purple-100 text-purple-800',
      'AJUSTE': 'bg-gray-100 text-gray-800'
    };
    return styles[type] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "dd MMM yyyy, HH:mm", { locale: es });
  };

  // Filter commissions for non-admin users
  const visibleTransactions = isAdmin() 
    ? transactions 
    : transactions.filter(t => t.transaction_type !== 'COMISION');

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner w-10 h-10"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="wallet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Wallet className="w-8 h-8 text-blue-600" />
              Wallet de {gestor?.name}
            </h1>
            <p className="text-slate-500 mt-1">Estado de cuenta y transacciones</p>
          </div>
        </div>
        {isAdmin() && (
          <Button 
            onClick={() => setIsAdjustmentDialogOpen(true)}
            className="bg-slate-700 hover:bg-slate-800"
            data-testid="adjustment-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Ajuste Manual
          </Button>
        )}
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-xl">
        <CardContent className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <p className="text-slate-400 text-sm uppercase tracking-wider">Saldo Actual</p>
              <p className={`text-4xl font-bold mt-2 ${summary?.current_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {summary?.current_balance >= 0 ? '+' : '-'}{formatCurrency(summary?.current_balance || 0)}
              </p>
              <p className="text-slate-400 text-sm mt-2">
                {summary?.current_balance >= 0 ? 'A favor del gestor' : 'Deuda pendiente'}
              </p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-lg p-4">
                <TrendingDown className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-slate-400 text-xs">Ventas</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.total_sales || 0)}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <ArrowDownLeft className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-slate-400 text-xs">Pagos</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.total_payments || 0)}</p>
              </div>
              {isAdmin() && (
                <div className="bg-white/10 rounded-lg p-4">
                  <Receipt className="w-5 h-5 text-orange-400 mb-2" />
                  <p className="text-slate-400 text-xs">Comisiones CEO</p>
                  <p className="text-lg font-semibold">{formatCurrency(summary?.total_commissions || 0)}</p>
                </div>
              )}
              <div className="bg-white/10 rounded-lg p-4">
                <Gift className="w-5 h-5 text-purple-400 mb-2" />
                <p className="text-slate-400 text-xs">Bonos</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.total_bonuses || 0)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filtrar:</span>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="wallet-type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="VENTA">Ventas</SelectItem>
                <SelectItem value="PAGO">Pagos</SelectItem>
                {isAdmin() && <SelectItem value="COMISION">Comisiones</SelectItem>}
                <SelectItem value="BONO">Bonos</SelectItem>
                <SelectItem value="AJUSTE">Ajustes</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-auto">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.start ? (
                    dateRange.end ? `${format(dateRange.start, 'dd/MM')} - ${format(dateRange.end, 'dd/MM')}` : format(dateRange.start, 'dd/MM/yyyy')
                  ) : 'Rango de fechas'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="range" selected={{ from: dateRange.start, to: dateRange.end }} onSelect={(range) => setDateRange({ start: range?.from, end: range?.to })} locale={es} numberOfMonths={2} />
                <div className="p-2 border-t flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setDateRange({ start: null, end: null })}>Limpiar</Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={fetchData} title="Refrescar">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-500 ml-auto">{visibleTransactions.length} transacciones</span>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-slate-600" />
            Historial de Transacciones
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {visibleTransactions.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No hay transacciones en este período</div>
          ) : (
            <div className="divide-y">
              {visibleTransactions.map((t) => (
                <div key={t.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between" data-testid={`transaction-${t.id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      t.transaction_type === 'COMISION' ? 'bg-orange-100' :
                      t.amount >= 0 ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {getTransactionIcon(t.transaction_type, t.amount)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={getTransactionBadge(t.transaction_type)}>{t.transaction_type}</Badge>
                        <span className="text-xs text-slate-500">{formatDate(t.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{t.description || 'Sin descripción'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${
                        t.transaction_type === 'COMISION' ? 'text-orange-600' :
                        t.amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {t.transaction_type === 'COMISION' 
                          ? `-${formatCurrency(t.commission_amount || 0)}`
                          : `${t.amount >= 0 ? '+' : '-'}${formatCurrency(t.amount)}`
                        }
                      </p>
                      <p className="text-xs text-slate-500">Saldo: {t.balance_after >= 0 ? '+' : '-'}{formatCurrency(t.balance_after)}</p>
                    </div>
                    {isAdmin() && (
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-txn-${t.id}`}
                          onClick={() => { setEditingTxn(t); setEditForm({ amount: String(t.amount), description: t.description || '' }); }}>
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`delete-txn-${t.id}`}
                          onClick={() => setDeleteConfirm(t)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjustment Dialog */}
      <Dialog open={isAdjustmentDialogOpen} onOpenChange={setIsAdjustmentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-600" />
              Ajuste Manual de Wallet
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Positivo (+):</strong> Crédito a favor del gestor<br/>
                <strong>Negativo (-):</strong> Débito / aumenta deuda
              </p>
            </div>
            <div className="space-y-2">
              <Label>Monto del Ajuste (USD) *</Label>
              <Input type="number" step="0.01" value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                placeholder="+100 o -100" required className="bg-slate-50" data-testid="adjustment-amount" />
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Input value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, description: e.target.value })}
                placeholder="Corrección por error, descuento especial..." required className="bg-slate-50" data-testid="adjustment-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAdjustmentDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-700 hover:bg-slate-800">
                {submitting ? 'Guardando...' : 'Aplicar Ajuste'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editingTxn} onOpenChange={() => setEditingTxn(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Transacción</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTxn} className="space-y-4">
            <div className="space-y-2">
              <Label>Monto (USD)</Label>
              <Input type="number" step="0.01" value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                className="bg-slate-50" data-testid="edit-txn-amount" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-slate-50" data-testid="edit-txn-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingTxn(null)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Transacción</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de eliminar esta transacción? Esta acción recalculará todos los saldos.
          </p>
          {deleteConfirm && (
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm"><strong>Tipo:</strong> {deleteConfirm.transaction_type}</p>
              <p className="text-sm"><strong>Monto:</strong> {deleteConfirm.amount >= 0 ? '+' : ''}{formatCurrency(deleteConfirm.amount)}</p>
              <p className="text-sm"><strong>Descripción:</strong> {deleteConfirm.description}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => handleDeleteTxn(deleteConfirm.id)} data-testid="confirm-delete-txn">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletPage;
